import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';

const Verifications = () => {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => { fetchVerifications(); }, []);
  useEffect(() => { setCurrentPage(1); }, [filter]);

  const fetchVerifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/verifications`);
      setVerifications(response.data);
    } catch (error) {
      console.error('Error fetching verifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVerifications = filter === 'all' ? verifications
    : filter === 'with_matches' ? verifications.filter(v => v.matches_found > 0)
    : verifications.filter(v => v.matches_found === 0);

  const totalPages = Math.ceil(filteredVerifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentVerifications = filteredVerifications.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const tabs = [
    { id: 'all', label: 'All', count: verifications.length },
    { id: 'with_matches', label: 'With Matches', count: verifications.filter(v => v.matches_found > 0).length },
    { id: 'no_matches', label: 'No Matches', count: verifications.filter(v => v.matches_found === 0).length },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header + filter tabs */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Verifications</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filteredVerifications.length} of {verifications.length} records</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === tab.id ? 'bg-white shadow-sm text-violet-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              {tab.label} <span className={`ml-1 text-xs ${filter === tab.id ? 'text-violet-500' : 'text-slate-400'}`}>({tab.count})</span>
            </button>
          ))}
        </div>
      </div>

      {filteredVerifications.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <p className="text-slate-400 text-sm">No verifications found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {currentVerifications.map((v) => (
              <div
                key={v.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                onClick={() => setSelectedVerification(v)}
              >
                <div className="relative aspect-square">
                  <img
                    src={`${API_URL}/verifications/${v.id}/thumbnail`}
                    alt="Verification"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const fallback = `${API_URL}/verifications/${v.id}/image`;
                      if (e.target.src !== fallback) { e.target.src = fallback; }
                      else {
                        const staticUrl = `${API_BASE_URL}/${v.filepath}`;
                        if (e.target.src !== staticUrl) e.target.src = staticUrl;
                        else e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23e2e8f0" width="200" height="200"/%3E%3C/svg%3E';
                      }
                    }}
                  />
                  <div className="absolute top-2 right-2">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full ${v.matches_found > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-500 text-white'}`}>
                      {v.matches_found > 0 ? `${v.matches_found} ✓` : '0'}
                    </span>
                  </div>
                </div>
                <div className="px-3 py-2">
                  <p className="text-xs font-semibold text-slate-800 truncate">{v.user_name}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{new Date(v.created_at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1.5">
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                if (pg === 1 || pg === totalPages || (pg >= currentPage - 1 && pg <= currentPage + 1)) {
                  return (
                    <button key={pg} onClick={() => goToPage(pg)}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${currentPage === pg ? 'text-white shadow-sm' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                      style={currentPage === pg ? { background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' } : {}}>
                      {pg}
                    </button>
                  );
                } else if (pg === currentPage - 2 || pg === currentPage + 2) {
                  return <span key={pg} className="text-slate-400 text-sm">…</span>;
                }
                return null;
              })}
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
                →
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selectedVerification && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedVerification(null)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-slate-900">Verification Details</h2>
                <button onClick={() => setSelectedVerification(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <img
                src={`${API_URL}/verifications/${selectedVerification.id}/image`}
                alt="Verification"
                className="w-full rounded-2xl object-contain max-h-64 bg-slate-50 mb-4"
                onError={(e) => {
                  const fallback = `${API_BASE_URL}/${selectedVerification.filepath}`;
                  if (e.target.src !== fallback) e.target.src = fallback;
                }}
              />
              <div className="space-y-1.5 mb-4">
                {[
                  { label: 'User', val: selectedVerification.user_name },
                  { label: 'Email', val: selectedVerification.user_email },
                  { label: 'Phone', val: selectedVerification.user_phone },
                  { label: 'Date', val: new Date(selectedVerification.created_at).toLocaleString() },
                ].map(({ label, val }) => val ? (
                  <div key={label} className="flex gap-2 text-sm">
                    <span className="text-slate-400 w-16 flex-shrink-0">{label}</span>
                    <span className="text-slate-800 font-medium">{val}</span>
                  </div>
                ) : null)}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Matches', val: selectedVerification.matches_found, cls: selectedVerification.matches_found > 0 ? 'text-emerald-700' : 'text-slate-700' },
                  selectedVerification.top_match_score ? { label: 'Top Score', val: `${selectedVerification.top_match_score}%`, cls: 'text-violet-700' } : null,
                  selectedVerification.threshold_used ? { label: 'Threshold', val: `${selectedVerification.threshold_used}%`, cls: 'text-slate-700' } : null,
                ].filter(Boolean).map(({ label, val, cls }) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-3 text-center">
                    <p className={`text-xl font-bold ${cls}`}>{val}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Verifications;
